import { Currency } from "@redotech/money/currencies";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { ReturnSummaryTotalsSchema } from "@redotech/redo-return-core-common/returns/return-totals/types";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/** Server-computed status band for the POS confirmation header. */
// Coincidental value-set match with the unrelated AmazonFeedGroupResultCode CRDB enum. //redontIgnore
export enum PosConfirmationTopStatus {
  Success = "success",
  Warning = "warning",
  Error = "error",
}

/** One row in the confirmation item lists; prices arrive display-ready. */
export const PosConfirmationItemSchema = z.object({
  id: z.string(),
  pendingReturnItemId: z.string(),
  imageSrc: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  quantity: z.number(),
  formattedPrice: z.string(),
  price: z.string(),
  itemValue: z.string(),
  currency: createEnum(Currency),
  presentmentCurrency: z.string(),
  exchangeRate: z.number(),
});

/** Non-actionable notice shown with the confirmation header. */
export const PosConfirmationAlertSchema = z.object({
  title: z.string(),
  subtitle: z.string(),
});

/**
 * Display-ready confirmation screen state, computed server-side (replaces
 * the client-side useReturnConfirmation fork in pos-ui-v2).
 */
export const PosConfirmationViewSchema = z.object({
  header: z.object({
    title: z.string(),
    subtitle: z.string(),
    status: createEnum(PosConfirmationTopStatus),
    actionTitle: z.string().nullable(),
    actionUrl: z.string().nullable(),
    alerts: z.array(PosConfirmationAlertSchema).nullable(),
  }),
  returnType: createEnum(ReturnTypeEnum),
  rejected: z.boolean(),
  rejectHtml: z.string(),
  /** Merchant theme `custom_confirmation_text`, if configured. */
  customSuccessMessage: z.string().nullable(),
  /** Customer-visible warnings (already filtered), with support codes. */
  warnings: z.array(
    z.object({ code: z.string(), message: z.string().nullable() }),
  ),
  deadlineDays: z.number(),
  showDeadline: z.boolean(),
  currency: createEnum(Currency),
  presentmentCurrency: z.string(),
  exchangeRate: z.number(),
  products: z.array(PosConfirmationItemSchema),
  newItems: z.array(PosConfirmationItemSchema),
  newItemsHeader: z.string(),
  returnItemsHeader: z.string(),
  summaryFeesHeader: z.string(),
  summaryFeesOrderHeader: z.string(),
  texts: z.object({
    rejectedText: z.string(),
    returnItems: z.string(),
    newItems: z.string(),
  }),
  customerInfo: z.object({
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    id: z.string(),
  }),
  draftOrderUrl: z.string().nullable(),
  labelDeducted: z.boolean(),
  shouldShowSummary: z.boolean(),
  manualLabelInfo: z.object({
    showManualLabel: z.boolean(),
    instructions: z.array(z.string()).nullable(),
  }),
  showReturnSteps: z.boolean(),
  /** Refund/store-credit copy line; null when there is nothing to show. */
  refundDetailsText: z.string().nullable(),
  // Passed through for the summary/cart/next-steps children.
  returnTotals: ReturnSummaryTotalsSchema,
});

export type PosConfirmationView = z.infer<typeof PosConfirmationViewSchema>;
export type PosConfirmationItem = z.infer<typeof PosConfirmationItemSchema>;

export const getPosConfirmationView = {
  name: "getPosConfirmationView" as const,
  input: z.object({ returnId: z.string() }),
  output: z.object({ view: PosConfirmationViewSchema }),
};
